import React, { useEffect, useState, useCallback } from "react";
import api, { describeError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

const ROLES = [{ value: "firm_admin", label: "Firm Admin" }, { value: "advocate", label: "Advocate" }, { value: "staff", label: "Staff" }];
const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;

export default function Team() {
  const { user, hasFeature } = useAuth();
  const canManage = hasFeature("team_management");
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", role: "advocate" });
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/team"); setMembers(data); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const add = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/team", form);
      toast.success(`${data.name} added. Temp password: ${data.temp_password}`, { duration: 10000 });
      setOpen(false); setForm({ name: "", email: "", mobile: "", role: "advocate" }); load();
    } catch (err) { toast.error(describeError(err, "Could not add member.")); }
    finally { setSaving(false); }
  };

  const changeRole = async (m, role) => {
    try { await api.put(`/team/${m.id}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(describeError(err, "Could not update role.")); }
  };
  const toggleActive = async (m) => {
    try { await api.put(`/team/${m.id}`, { active: !m.active }); toast.success(m.active ? "Deactivated" : "Activated"); load(); }
    catch (err) { toast.error(describeError(err, "Could not update user.")); }
  };
  const remove = async () => {
    try { await api.delete(`/team/${toRemove.id}`); toast.success("Member removed"); load(); }
    catch (err) { toast.error(describeError(err, "Could not remove member.")); }
    finally { setToRemove(null); }
  };

  return (
    <div data-testid="team-page">
      <PageHeader title="Team" subtitle="Manage advocates and staff in your firm."
        actions={<Button onClick={() => setOpen(true)} disabled={!canManage} data-testid="add-member-btn" className="bg-gold text-navy hover:bg-gold-dark"><UserPlus className="mr-1.5 h-4 w-4" />Add Member</Button>} />

      {!canManage && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm text-navy" data-testid="team-upgrade-banner">
          <UserPlus className="h-4 w-4 text-gold-dark" />
          Team management is available on the Law Firm plan. Upgrade to add advocates and staff.
        </div>
      )}

      {!members ? <LoadingState /> : (
        <Card className="overflow-hidden border-border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th><th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Joined</th><th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {members.filter((m) => m.role !== "client").map((m) => (
                  <tr key={m.id} data-testid={`team-row-${m.id}`} className="hover:bg-secondary/40">
                    <td className="px-4 py-3 font-medium text-navy">{m.name}{m.id === user.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.email}</td>
                    <td className="px-4 py-3">
                      <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`team-role-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-500"}`}>{m.active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(m.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => toggleActive(m)} data-testid={`team-toggle-${m.id}`}>{m.active ? "Deactivate" : "Activate"}</Button>
                        <Button size="sm" variant="outline" onClick={() => setToRemove(m)} data-testid={`team-remove-${m.id}`} className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Add Team Member</DialogTitle>
            <DialogDescription>A temporary password will be generated to share with them.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Full Name *</Label><Input data-testid="member-name-input" value={form.name} onChange={set("name")} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Email *</Label><Input type="email" data-testid="member-email-input" value={form.email} onChange={set("email")} /></div>
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Mobile</Label><Input value={form.mobile} onChange={set("mobile")} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={set("role")}><SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter><Button onClick={add} disabled={saving} data-testid="member-save-btn" className="bg-navy text-white hover:bg-navy-light">{saving ? "Adding…" : "Add Member"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {toRemove?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They will lose access to this workspace. Users with assigned cases must be reassigned or deactivated instead.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="team-remove-confirm" className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
